import React, { useEffect, useState } from 'react';
import { Formik, Form } from "formik";
import { FormikInput, FormikTextarea, Button } from "../UI";
import { NewAEMAdobeIOFormSchema } from '../../schemas';
import { useUserContext } from '../../contexts/UserContext';
import { addNewAEMAdobeIO, getAllAEMAdobeIO, updateSpecificAEMAdobeIO } from '../../utils/APIs';
import Loader from '../UI/Loader/Loader';
import { useNavigate, useParams } from 'react-router-dom';
import backwardArrow from '../../assets/icons/backward-arrow.svg';
import { toast } from 'react-toastify';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { Eye, EyeOff } from 'lucide-react';
const NewAEMAdobeIOIntegration = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { userData, updateDefaultAEMAdobeIO, updateAEMAdobeIOIntegrations } = useUserContext();
  const { content } = useContentfulContext();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();
  const [showPassword, setShowPassword] = useState({
    clientSecret: false,
    privateKey: false,
  });

  const toggleShowPassword = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  useEffect(() => {
    getProductData();
  }, [userData, id]);

  const getProductData = async () => {
    try {
      setLoading(true);
      if (id && userData?.selectedOrganization?.org_token) {
        const result = await getAllAEMAdobeIO(
          userData.token,
          userData.selectedOrganization.org_token,
          id
        );
        setData(result?.data[0]);
      }
    } catch (error) {
      console.log("Error fetching product data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values, actions) => {
    try {
      setLoading(true);

      const data = {
        ...values,
        technicalAccount: {
          clientId: values.clientId,
          clientSecret: values.clientSecret,
        }
      };
      delete data.clientId;
      delete data.clientSecret;

      let result;
      if (id) {
        result = await updateSpecificAEMAdobeIO(
          userData.token,
          userData.selectedOrganization.org_token,
          {
            id,
            bodyData: data
          }
        )
      } else {
        result = await addNewAEMAdobeIO(userData.token, {
          organizationId: userData.selectedOrganization.org_token,
          data,
        });
      }
      !loading && toast.success(content.NEW_AEM_ADOBE_IO_CONTENT.success);
      updateAEMAdobeIOIntegrations()
      if (!userData.defaultAEMAdobeIO) {
        updateDefaultAEMAdobeIO(result.data)
      }
      setTimeout(() => {
        navigate('/setup/aem-adobe-io');
      }, 1000);
    } catch (error) {
      toast.error(error?.message || "default error");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div class="w-full px-4 md:px-8 my-6">
      {loading && <Loader />}
      <div class="flex flex-col lg:w-1/2 justify-center items-start">
        <div class="space-y-4 md:space-y-6 w-full">
          <p class="font-medium cursor-pointer text-primary hover:underline flex items-center" onClick={(e) => {
            e.preventDefault();
            navigate('/setup/aem-adobe-io')
          }}>
            <img src={backwardArrow} alt='' className='w-5 h-5 mr-2' /> {content.NEW_AEM_ADOBE_IO_CONTENT.back_cta_text}
          </p>
          <Formik
            initialValues={{
              aem_adobe_io_name: data?.aem_adobe_io_name || "",
              imsEndpoint: data?.imsEndpoint || "",
              metascopes: data?.metascopes || "",
              clientId: data?.technicalAccount?.clientId || "",
              clientSecret: data?.technicalAccount?.clientSecret || "",
              email: data?.email || "",
              id: data?.id || "",
              org: data?.org || "",
              privateKey: data?.privateKey || "",
              publicKey: data?.publicKey || "",
              certificateExpirationDate: data?.certificateExpirationDate || "",
              author_api_url: data?.author_api_url || "",
            }}
            enableReinitialize={true}
            validationSchema={() => NewAEMAdobeIOFormSchema(content.NEW_AEM_ADOBE_IO_CONTENT)}
            onSubmit={handleSubmit}
          >
            {() => (
              <Form action="#" className="space-y-4">
                <h2 className="text-xl font-bold leading-tight tracking-tight text-black text-left mb-4">{content.NEW_AEM_ADOBE_IO_CONTENT.title}</h2>
                <p>{content.NEW_AEM_ADOBE_IO_CONTENT.description}</p>

                <FormikInput
                  type="text"
                  id="aem_adobe_io_name"
                  name="aem_adobe_io_name"
                  label={content.NEW_AEM_ADOBE_IO_CONTENT.formLabels.aem_adobe_io_name}
                  helperText={content.NEW_AEM_ADOBE_IO_CONTENT.help_text.aem_adobe_io_name}
                />

                <FormikInput
                  type="text"
                  id="imsEndpoint"
                  name="imsEndpoint"
                  label={content.NEW_AEM_ADOBE_IO_CONTENT.formLabels.imsEndpoint}
                  helperText={content.NEW_AEM_ADOBE_IO_CONTENT.help_text.imsEndpoint}
                />

                <FormikInput
                  type="text"
                  id="metascopes"
                  name="metascopes"
                  label={content.NEW_AEM_ADOBE_IO_CONTENT.formLabels.metascopes}
                  helperText={content.NEW_AEM_ADOBE_IO_CONTENT.help_text.metascopes}
                />

                <FormikInput
                  type="text"
                  id="clientId"
                  name="clientId"
                  label={content.NEW_AEM_ADOBE_IO_CONTENT.formLabels.clientId}
                  helperText={content.NEW_AEM_ADOBE_IO_CONTENT.help_text.clientId}
                />

                <FormikInput
                  type={showPassword.clientSecret ? "text" : "password"}
                  id="clientSecret"
                  name="clientSecret"
                  label={content.NEW_AEM_ADOBE_IO_CONTENT.formLabels.clientSecret}
                  helperText={content.NEW_AEM_ADOBE_IO_CONTENT.help_text.clientSecret}
                  suffixIcon={
                    <button
                      type="button"
                      onClick={() => toggleShowPassword("clientSecret")}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none"
                      aria-label={showPassword.clientSecret ? "Hide client secret" : "Show client secret"}
                    >
                      {showPassword.clientSecret ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  }
                />

                <FormikInput
                  type="text"
                  id="email"
                  name="email"
                  label={content.NEW_AEM_ADOBE_IO_CONTENT.formLabels.email}
                  helperText={content.NEW_AEM_ADOBE_IO_CONTENT.help_text.email}
                />

                <FormikInput
                  type="text"
                  id="id"
                  name="id"
                  label={content.NEW_AEM_ADOBE_IO_CONTENT.formLabels.id}
                  helperText={content.NEW_AEM_ADOBE_IO_CONTENT.help_text.id}
                />

                <FormikInput
                  type="text"
                  id="org"
                  name="org"
                  label={content.NEW_AEM_ADOBE_IO_CONTENT.formLabels.org}
                  helperText={content.NEW_AEM_ADOBE_IO_CONTENT.help_text.org}
                />

                <div className="relative">
                  <FormikTextarea
                    id="privateKey"
                    name="privateKey"
                    label={content.NEW_AEM_ADOBE_IO_CONTENT.formLabels.privateKey}
                    helperText={content.NEW_AEM_ADOBE_IO_CONTENT.help_text.privateKey}
                    rows={4}
                    className={showPassword.privateKey ? "" : "blur-text"}
                    style={showPassword.privateKey ? {} : { WebkitTextSecurity: "disc", fontFamily: "text-security-disc" }}
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowPassword("privateKey")}
                    className="absolute right-3 top-9 text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label={showPassword.privateKey ? "Hide private key" : "Show private key"}
                  >
                    {showPassword.privateKey ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                <FormikTextarea
                  id="publicKey"
                  name="publicKey"
                  label={content.NEW_AEM_ADOBE_IO_CONTENT.formLabels.publicKey}
                  helperText={content.NEW_AEM_ADOBE_IO_CONTENT.help_text.publicKey}
                  rows={4}
                />

                <FormikInput
                  type="text"
                  id="certificateExpirationDate"
                  name="certificateExpirationDate"
                  label={content.NEW_AEM_ADOBE_IO_CONTENT.formLabels.certificateExpirationDate}
                  helperText={content.NEW_AEM_ADOBE_IO_CONTENT.help_text.certificateExpirationDate}
                />

                <FormikInput
                  type="text"
                  id="author_api_url"
                  name="author_api_url"
                  label={content.NEW_AEM_ADOBE_IO_CONTENT.formLabels.author_api_url}
                  helperText={content.NEW_AEM_ADOBE_IO_CONTENT.help_text.author_api_url}
                />

                <div className="pt-2">
                  <Button type="submit" variant="primary" className="w-full md:w-auto">
                    {content.NEW_AEM_ADOBE_IO_CONTENT.submit_cta}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  )
}

export default NewAEMAdobeIOIntegration;